import { Link } from "react-router-dom";
import Reveal from "@/components/motion/Reveal";
import KineticLines from "@/components/motion/KineticLines";
import {
  Snake,
  Turtle,
  Gecko,
  Fern,
  Rose,
  Ghost,
  Mushroom,
  Leaf,
} from "@/components/decor/SvgIcons";
import { ArrowRight, PawPrint, ChatCircleText, HeartStraight } from "@phosphor-icons/react";

export default function Adoptables() {
  return (
    <div className="pt-28">
      {/* Hero */}
      <section className="relative overflow-hidden pb-14" data-testid="adoptables-hero">
        <div className="absolute top-16 right-8 opacity-80 hidden md:block"><Ghost size={64} /></div>
        <div className="absolute top-40 left-4 opacity-70 hidden md:block"><Fern size={90} /></div>
        <div className="absolute bottom-8 right-1/3 opacity-70 hidden md:block"><Rose size={64} /></div>

        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="chip mb-6">A future chapter</div>
          <KineticLines
            lines={["Adoptable", "Animals."]}
            italicIdx={1}
            className="font-display text-forest text-[13vw] md:text-[8vw] lg:text-[7rem] leading-[0.96] tracking-tight"
          />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-2xl text-lg text-forest/85">
              Looking for a scaled friend to grow your family? We'd love to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Message card */}
      <section className="pb-24" data-testid="adoptables-message">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10">
          <div className="grim-card relative overflow-hidden p-10 md:p-16">
            <div className="absolute -top-6 -right-6 opacity-70"><Snake size={110} className="rotate-12" /></div>
            <div className="absolute -bottom-4 -left-4 opacity-70"><Mushroom size={80} /></div>

            <div className="chip mb-6">Coming Soon</div>
            <h2 className="font-display text-4xl md:text-5xl text-forest leading-[1.05]">
              Our adoption program is <em className="text-plum">still in development.</em>
            </h2>

            <div className="mt-8 space-y-5 text-forest/85 text-lg leading-relaxed max-w-2xl">
              <p>
                Spooky Scales hasn't yet begun formally placing animals into new homes — but it is
                a program we intend to build with great care. Our goal is to responsibly match
                rescued reptiles and amphibians with prepared, educated forever families.
              </p>
              <p>
                In the meantime, if you're considering welcoming a scaled friend or family member
                into your home — and you're unsure where to begin, or which species would be the
                right fit for your life — <strong>we'd love to talk with you.</strong>
              </p>
              <p>
                Reach out anytime. Even before our adoption program opens, we're happy to offer
                honest guidance, species recommendations, husbandry consultation, and a warm hand
                on the journey.
              </p>
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-pill btn-primary" data-testid="adoptables-cta-contact">
                Get in touch <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/resources" className="btn-pill btn-secondary" data-testid="adoptables-cta-resources">
                See our resources
              </Link>
            </div>
          </div>

          {/* Three friendly info blocks */}
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {[
              {
                Icon: ChatCircleText,
                title: "Species guidance",
                copy: "Honest advice on matching a species to your space, schedule, and experience level.",
              },
              {
                Icon: PawPrint,
                title: "Habitat planning",
                copy: "Help designing an ethical, enriched enclosure before you bring anyone home.",
              },
              {
                Icon: HeartStraight,
                title: "Long-term support",
                copy: "Follow-up questions, husbandry check-ins, and behavior troubleshooting — always welcome.",
              },
            ].map((c) => (
              <div key={c.title} className="grim-card p-6">
                <div className="w-11 h-11 rounded-full bg-sage/40 flex items-center justify-center text-forest">
                  <c.Icon size={22} weight="duotone" />
                </div>
                <h3 className="font-display text-2xl text-forest mt-4">{c.title}</h3>
                <p className="mt-2 text-forest/80 text-sm leading-relaxed">{c.copy}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 flex items-center justify-center gap-6 opacity-70">
            <Turtle size={50} />
            <Leaf size={30} className="rotate-45" />
            <Gecko size={60} />
          </div>
        </div>
      </section>
    </div>
  );
}
